import React, { useState, useRef } from "react";
import { CustomLink, SocialLink, SocialPlatform } from "@/types/linkinbio";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Instagram, Globe, Plus, Trash2, Move, Link, Upload, X, Loader2 } from "lucide-react";
import { SupabaseStorage } from "@/lib/supabase-storage";
import { supabase } from "@/integrations/supabase/client";
import { shadcnToast } from "@/hooks/use-toast";

interface LinksSectionProps {
  links: CustomLink[];
  socialLinks: SocialLink[];
  onAddLink: () => void;
  onUpdateLink: (link: CustomLink) => void;
  onRemoveLink: (linkId: string) => void;
  onReorderLinks: (links: CustomLink[]) => void;
  onSocialLinksUpdate: (socialLinks: SocialLink[]) => void;
}

export const LinksSection: React.FC<LinksSectionProps> = ({
  links,
  socialLinks,
  onAddLink,
  onUpdateLink,
  onRemoveLink,
  onReorderLinks,
  onSocialLinksUpdate,
}) => {
  const [draggedLinkId, setDraggedLinkId] = useState<string | null>(null);
  const [uploadingIcons, setUploadingIcons] = useState<Set<string>>(new Set());
  const iconInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const getSocialIcon = (platform: SocialPlatform) => {
    switch (platform) {
      case 'instagram': return <Instagram size={18} />;
      case 'website': return <Globe size={18} />;
      default: return null;
    }
  };

  const getSocialPlatformName = (platform: SocialPlatform) => {
    switch (platform) {
      case 'instagram': return 'Instagram';
      case 'website': return 'Website';
      default: return platform;
    }
  };

  const handleSocialLinkChange = (platform: SocialPlatform, url: string) => {
    const updatedLinks = [...socialLinks];
    const existingLinkIndex = updatedLinks.findIndex(link => link.platform === platform);
    
    if (url) {
      if (existingLinkIndex >= 0) {
        updatedLinks[existingLinkIndex] = { platform, url };
      } else {
        updatedLinks.push({ platform, url });
      }
    } else {
      if (existingLinkIndex >= 0) {
        updatedLinks.splice(existingLinkIndex, 1);
      }
    }
    
    onSocialLinksUpdate(updatedLinks);
  };

  const getSocialLinkValue = (platform: SocialPlatform) => {
    return socialLinks.find(link => link.platform === platform)?.url || '';
  };

  const handleDragStart = (linkId: string) => {
    setDraggedLinkId(linkId);
  };

  const handleDragOver = (e: React.DragEvent, targetLinkId: string) => {
    e.preventDefault();
    if (!draggedLinkId || draggedLinkId === targetLinkId) return;

    const draggedIndex = links.findIndex(link => link.id === draggedLinkId);
    const targetIndex = links.findIndex(link => link.id === targetLinkId);
    
    if (draggedIndex === targetIndex) return;
    
    const updatedLinks = [...links];
    const draggedItem = updatedLinks[draggedIndex];
    
    updatedLinks.splice(draggedIndex, 1);
    updatedLinks.splice(targetIndex, 0, draggedItem);
    
    // Update order property
    updatedLinks.forEach((link, index) => {
      link.order = index;
    });
    
    onReorderLinks(updatedLinks);
  };

  const handleDragEnd = () => {
    setDraggedLinkId(null);
  };
  
  const handleIconUpload = async (linkId: string, file: File) => {
    try {
      setUploadingIcons(prev => new Set(prev).add(linkId));
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      // Find the link to get current icon
      const link = links.find(l => l.id === linkId);
      
      // Delete old icon if exists
      if (link?.icon) {
        const oldPath = SupabaseStorage.getPathFromUrl(link.icon);
        if (oldPath) {
          await SupabaseStorage.deleteImage(oldPath);
        }
      }
      
      // Generate new path and upload
      const iconPath = SupabaseStorage.generateLinkIconPath(user.id, linkId);
      const iconUrl = await SupabaseStorage.uploadImage(file, iconPath);
      
      if (iconUrl && link) {
        onUpdateLink({ ...link, icon: iconUrl });
        shadcnToast({
          description: "Link icon uploaded successfully!"
        });
      } else {
        throw new Error('Failed to upload icon');
      }
    } catch (error: any) {
      console.error('Error uploading icon:', error);
      shadcnToast({
        description: error.message || "Failed to upload icon",
        variant: "destructive"
      });
    } finally {
      setUploadingIcons(prev => {
        const newSet = new Set(prev);
        newSet.delete(linkId);
        return newSet;
      });
      // Reset the input
      const inputRef = iconInputRefs.current.get(linkId);
      if (inputRef) {
        inputRef.value = '';
      }
    }
  };
  
  const handleRemoveIcon = async (linkId: string) => {
    try {
      const link = links.find(l => l.id === linkId);
      if (!link?.icon) return;
      
      // Delete from storage if it's a Supabase URL
      const iconPath = SupabaseStorage.getPathFromUrl(link.icon);
      if (iconPath) {
        await SupabaseStorage.deleteImage(iconPath);
      }
      
      onUpdateLink({ ...link, icon: undefined });
      shadcnToast({
        description: "Link icon removed successfully!"
      });
    } catch (error) {
      console.error('Error removing icon:', error);
      shadcnToast({
        description: "Failed to remove icon",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-medium mb-4">Social Media Links</h3>
        <div className="space-y-4">
          {(['instagram', 'website'] as SocialPlatform[]).map((platform) => (
            <div key={platform} className="flex items-center gap-3">
              <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full">
                {getSocialIcon(platform)}
              </div>
              <div className="flex-1">
                <Label htmlFor={`social-${platform}`} className="sr-only">
                  {getSocialPlatformName(platform)}
                </Label>
                <Input
                  id={`social-${platform}`}
                  placeholder={`Your ${getSocialPlatformName(platform)} URL`}
                  value={getSocialLinkValue(platform)}
                  onChange={(e) => handleSocialLinkChange(platform, e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Custom Links</h3>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onAddLink} 
            className="flex items-center gap-2"
          >
            <Plus size={14} />
            Add Link
          </Button>
        </div>
        
        {links.length === 0 ? (
          <div className="text-center py-8 border border-dashed rounded-lg">
            <p className="text-muted-foreground">
              You haven't added any links yet.
            </p>
            <Button 
              variant="secondary" 
              className="mt-4"
              onClick={onAddLink}
            >
              <Plus size={16} className="mr-2" />
              Add Your First Link
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {links.map((link) => (
              <div
                key={link.id}
                draggable
                onDragStart={() => handleDragStart(link.id)}
                onDragOver={(e) => handleDragOver(e, link.id)}
                onDragEnd={handleDragEnd}
                className={`border rounded-lg p-4 ${
                  draggedLinkId === link.id ? "opacity-50" : ""
                }`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div 
                    className="cursor-move p-1 text-gray-400 hover:text-gray-600"
                    title="Drag to reorder"
                  >
                    <Move size={18} />
                  </div>
                  <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full relative">
                    {link.icon ? (
                      <>
                        <img src={link.icon} alt="" className="w-6 h-6 rounded" />
                        <button 
                          onClick={() => handleRemoveIcon(link.id)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                          aria-label="Remove icon"
                        >
                          <X size={8} />
                        </button>
                      </>
                    ) : (
                      <Link size={14} />
                    )}
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="Link Title"
                      value={link.title}
                      onChange={(e) => 
                        onUpdateLink({ ...link, title: e.target.value })
                      }
                    />
                  </div>
                </div>
                
                <div className="pl-12">
                  <Input
                    placeholder="https://your-link.com"
                    value={link.url}
                    onChange={(e) => 
                      onUpdateLink({ ...link, url: e.target.value })
                    }
                    className="mb-3"
                  />
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">Link Icon</Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          ref={(el) => {
                            if (el) {
                              iconInputRefs.current.set(link.id, el);
                            }
                          }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleIconUpload(link.id, file);
                          }}
                          accept="image/*"
                          className="hidden"
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => iconInputRefs.current.get(link.id)?.click()}
                          disabled={uploadingIcons.has(link.id)}
                        >
                          {uploadingIcons.has(link.id) ? (
                            <>
                              <Loader2 size={12} className="mr-1 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload size={12} className="mr-1" />
                              {link.icon ? 'Change' : 'Upload'} Icon
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Switch 
                          id={`link-active-${link.id}`}
                          checked={link.enabled}
                          onCheckedChange={(checked) => 
                            onUpdateLink({ ...link, enabled: checked })
                          }
                        />
                        <Label htmlFor={`link-active-${link.id}`}>Active</Label>
                      </div>
                      
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => onRemoveLink(link.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
